/**
 * Abschnitt „Wasser- und Klimaschutz"
 *
 * Zwei Themen, die beide der Anlagenhersteller vorgibt und die je Anlage genau einmal vorkommen:
 *
 * - Rückhaltung wassergefährdender Stoffe (ELiA 11.5). Die Windenergieanlage selbst ist die
 *   HBV-Anlage; der „Auffangraum" ist kein gebautes Becken, sondern Maschinenhaus und Turmfuß.
 * - Anlage mit fluorierten Treibhausgasen (ELiA 15.2) – die Mittelspannungsschaltanlage im
 *   Turmfuß, bei etlichen Anlagentypen SF6-isoliert.
 */
import React from "react";
import { Box, Typography } from "@mui/material";
import SubSection, { subSectionFullWidth } from "../../../layout/SubSection";
import InputField from "../../../common/inputs/InputField";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import Dropdown from "../../../common/inputs/Dropdown";
import ToggleChip from "../../../common/inputs/ToggleChip";
import { colors, fonts } from "../../../../styles/theme";
import {
  AuffangraumFelder,
  FGasFelder,
} from "../../../../services/api/anlagentypen";
import { WertelistenOptions } from "../hooks/useWertelisten";

type Werte = Partial<AuffangraumFelder & FGasFelder>;

interface WasserUndKlimaschutzFelderProps {
  werte: Werte;
  onChange: (feld: string, wert: string | number | boolean | null) => void;
  wertelisten: WertelistenOptions;
  readOnly?: boolean;
  defaultExpanded?: boolean;
}

/** Zwischenüberschrift innerhalb des Abschnitts – die beiden Themen bleiben unterscheidbar */
const Zwischentitel: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{ ...subSectionFullWidth, mt: 1 }}>
    <Typography
      sx={{
        fontFamily: fonts.primary,
        fontSize: "0.75rem",
        fontWeight: 600,
        color: colors.text.secondary,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {text}
    </Typography>
  </Box>
);

const WasserUndKlimaschutzFelder: React.FC<WasserUndKlimaschutzFelderProps> = ({
  werte,
  onChange,
  wertelisten,
  readOnly = false,
  defaultExpanded = false,
}) => {
  const zahl = (feld: keyof Werte) => {
    const wert = werte[feld];
    return wert === null || wert === undefined ? "" : String(wert);
  };
  const kennzeichen = (feld: keyof Werte) => werte[feld] === true;

  return (
    <SubSection
      title="Wasser- und Klimaschutz"
      defaultExpanded={defaultExpanded}
      columns={2}
    >
      <Zwischentitel text="Rückhaltung wassergefährdender Stoffe" />

      <InputFieldNumber
        label="Größtes freisetzbares Volumen"
        value={zahl("groesstes_freisetzbares_volumen_m3")}
        onChange={(v) => onChange("groesstes_freisetzbares_volumen_m3", v)}
        readOnly={readOnly}
        suffix="m³"
        variant="floating"
        tooltip="Größte Einzelfüllmenge der Anlage, in der Regel das Getriebeöl."
      />
      <InputFieldNumber
        label="Rückhaltevolumen"
        value={zahl("rueckhaltevolumen_m3")}
        onChange={(v) => onChange("rueckhaltevolumen_m3", v)}
        readOnly={readOnly}
        suffix="m³"
        variant="floating"
      />
      <InputFieldNumber
        label="Grundfläche des Auffangraumes"
        value={zahl("grundflaeche_m2")}
        onChange={(v) => onChange("grundflaeche_m2", v)}
        readOnly={readOnly}
        suffix="m²"
        variant="floating"
      />
      <Dropdown
        label="Gefährdungsstufe gem. § 39 AwSV"
        value={werte.gefaehrdungsstufe_id ?? ""}
        options={wertelisten.gefaehrdungsstufe}
        onChange={(v) => onChange("gefaehrdungsstufe_id", v || null)}
        disabled={readOnly}
        variant="floating"
      />

      <Zwischentitel text="Werkstoff des Auffangraumes" />
      <ToggleChip
        value={kennzeichen("werkstoff_stahl")}
        activeLabel="Stahl"
        inactiveLabel="Stahl"
        disabled={readOnly}
        onToggle={() =>
          onChange("werkstoff_stahl", !kennzeichen("werkstoff_stahl"))
        }
      />
      <ToggleChip
        value={kennzeichen("werkstoff_beton")}
        activeLabel="Beton"
        inactiveLabel="Beton"
        disabled={readOnly}
        onToggle={() =>
          onChange("werkstoff_beton", !kennzeichen("werkstoff_beton"))
        }
      />
      <ToggleChip
        value={kennzeichen("werkstoff_kunststoff")}
        activeLabel="Kunststoff"
        inactiveLabel="Kunststoff"
        disabled={readOnly}
        onToggle={() =>
          onChange("werkstoff_kunststoff", !kennzeichen("werkstoff_kunststoff"))
        }
      />
      <ToggleChip
        value={kennzeichen("werkstoff_sonstiges")}
        activeLabel="Sonstiges"
        inactiveLabel="Sonstiges"
        disabled={readOnly}
        onToggle={() =>
          onChange("werkstoff_sonstiges", !kennzeichen("werkstoff_sonstiges"))
        }
      />
      <InputField
        label="Werkstoff-Nr."
        value={werte.werkstoff_nummer ?? ""}
        onChange={(v) => onChange("werkstoff_nummer", v)}
        readOnly={readOnly}
        fullWidth
      />
      <InputField
        label="Material"
        value={werte.werkstoff_material ?? ""}
        onChange={(v) => onChange("werkstoff_material", v)}
        readOnly={readOnly}
        fullWidth
      />
      <ToggleChip
        value={kennzeichen("ist_beschichtet")}
        activeLabel="Beschichtet"
        inactiveLabel="Beschichtet"
        disabled={readOnly}
        onToggle={() =>
          onChange("ist_beschichtet", !kennzeichen("ist_beschichtet"))
        }
      />

      <Zwischentitel text="Fugen und Aufstellung" />
      <ToggleChip
        value={kennzeichen("hat_bauwerksfugen")}
        activeLabel="Bauwerksfugen"
        inactiveLabel="Bauwerksfugen"
        disabled={readOnly}
        onToggle={() =>
          onChange("hat_bauwerksfugen", !kennzeichen("hat_bauwerksfugen"))
        }
      />
      <InputField
        label="Material der Fugenabdichtung"
        value={werte.fugenabdichtung_material ?? ""}
        onChange={(v) => onChange("fugenabdichtung_material", v)}
        readOnly={readOnly}
        fullWidth
      />
      <ToggleChip
        value={kennzeichen("fugen_nachweis_beigefuegt")}
        activeLabel="Fugennachweis beigefügt"
        inactiveLabel="Fugennachweis beigefügt"
        disabled={readOnly}
        onToggle={() =>
          onChange(
            "fugen_nachweis_beigefuegt",
            !kennzeichen("fugen_nachweis_beigefuegt"),
          )
        }
      />
      {/*
        Beide Kennzeichen werden getrennt geführt und nicht voneinander abgeleitet – ELiA fragt
        sie einzeln ab. Vorbelegt wird keins: Getriebe, Hydraulik und Transformator stehen in
        Maschinenhaus und Turm, also wettergeschützt, aber ein Turm ist kein Gebäude im
        baurechtlichen Sinn. Welches Kreuz die Behörde erwartet, sagt weder die Ausfüllhilfe noch
        einer der ausgelegten Anträge – das weiß nur, wer den Antrag stellt.
      */}
      <Box sx={{ ...subSectionFullWidth }}>
        <Typography
          sx={{
            fontFamily: fonts.primary,
            fontSize: "0.75rem",
            color: colors.text.secondary,
          }}
        >
          Aufstellung: Die ölführenden Bauteile stehen in Maschinenhaus und
          Turm, also wettergeschützt – ein Turm ist aber kein Gebäude im
          baurechtlichen Sinn. Welches der beiden Kreuze die Behörde erwartet,
          ist mit ihr abzustimmen; wir belegen keins vor.
        </Typography>
      </Box>
      <ToggleChip
        value={kennzeichen("aufstellung_im_freien")}
        activeLabel="Im Freien"
        inactiveLabel="Im Freien"
        disabled={readOnly}
        onToggle={() =>
          onChange(
            "aufstellung_im_freien",
            !kennzeichen("aufstellung_im_freien"),
          )
        }
      />
      <ToggleChip
        value={kennzeichen("aufstellung_geschuetzt")}
        activeLabel="Überdacht oder geschützt"
        inactiveLabel="Überdacht oder geschützt"
        disabled={readOnly}
        onToggle={() =>
          onChange(
            "aufstellung_geschuetzt",
            !kennzeichen("aufstellung_geschuetzt"),
          )
        }
      />
      <ToggleChip
        value={kennzeichen("nach_trws_dwa_a_786")}
        activeLabel="Nach TRwS DWA-A 786"
        inactiveLabel="Nach TRwS DWA-A 786"
        disabled={readOnly}
        onToggle={() =>
          onChange("nach_trws_dwa_a_786", !kennzeichen("nach_trws_dwa_a_786"))
        }
      />
      <ToggleChip
        value={kennzeichen("nachweise_beigefuegt")}
        activeLabel="Nachweise beigefügt"
        inactiveLabel="Nachweise beigefügt"
        disabled={readOnly}
        onToggle={() =>
          onChange("nachweise_beigefuegt", !kennzeichen("nachweise_beigefuegt"))
        }
      />

      <Zwischentitel text="Fluorierte Treibhausgase" />
      <InputField
        label="Füllmittel"
        value={werte.fgas_fuellmittel ?? ""}
        onChange={(v) => onChange("fgas_fuellmittel", v)}
        readOnly={readOnly}
        fullWidth
        placeholder="bei Windenergie regelmäßig SF6"
      />
      <InputFieldNumber
        label="Füllmenge je Anlage"
        value={zahl("fgas_fuellmenge_kg")}
        onChange={(v) => onChange("fgas_fuellmenge_kg", v)}
        readOnly={readOnly}
        suffix="kg"
        variant="floating"
        tooltip="ELiA meint ausdrücklich die Füllmenge der einzelnen Anlage, nicht die Summe."
      />
      <ToggleChip
        value={kennzeichen("fgas_leckage_erkennung")}
        activeLabel="Leckage-Erkennung"
        inactiveLabel="Leckage-Erkennung"
        disabled={readOnly}
        onToggle={() =>
          onChange(
            "fgas_leckage_erkennung",
            !kennzeichen("fgas_leckage_erkennung"),
          )
        }
      />
      <Dropdown
        label="Intervall der Dichtheitsprüfung"
        value={werte.fgas_dichtheitspruefung_intervall_id ?? ""}
        options={wertelisten.dichtheitspruefung_intervall}
        onChange={(v) =>
          onChange("fgas_dichtheitspruefung_intervall_id", v || null)
        }
        disabled={readOnly}
        variant="floating"
      />
    </SubSection>
  );
};

export default WasserUndKlimaschutzFelder;
